/**
 * Generates a UUID v4 (Universally Unique Identifier).
 *
 * Algorithm:
 * 1. If `crypto.randomUUID()` is available — uses it (RFC 4122 compliant, cryptographically secure) via `uuidCrypto()`.
 * 2. Otherwise:
 *
 *    a. If `crypto.getRandomValues()` is available — generates random 4-bit numbers via `randomCrypto()`.
 *
 *    b. Otherwise — falls back to `Math.floor(Math.random() * 16)` via `randomMath()`.
 *
 *    These random values are then used to construct a UUID v4–like string with the pattern:
 *    `xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx`
 *
 * Explanation of the pattern:
 * - `x` → replaced with a random hexadecimal digit (`0`–`f`).
 * - `4` → fixed value indicating **version 4** of the UUID (random-based).
 * - `y` → replaced with a random hexadecimal digit from `8`, `9`, `a`, or `b`
 *         — the two most significant bits are set to `10` (per RFC 4122 variant 1).
 *
 * Explanation of numeric operations in `getRandom`:
 * - `randomCrypto()` → secure random number 0–15 (4 bits) from `crypto.getRandomValues`.
 * - `randomMath()` → fallback random number 0–15 when `crypto.getRandomValues` is unavailable.
 * - `(r & 0x3) | 0x8` → ensures the high bits of the `y` position are `10` in binary, matching UUID variant 1.
 * - `.toString(16)` → converts the number to hexadecimal.
 *
 * @returns {string} A valid UUID v4 string.
 *
 * @example
 * uuidv4(); // "3b12f1df-5232-4f29-8a3e-917bf397618a"
 */

const uuidCrypto = (): string | null => {
  return typeof crypto?.randomUUID === 'function' ? crypto.randomUUID() : null;
};

const randomCrypto = (): number => {
  if (crypto?.getRandomValues === undefined) {
    throw new Error('crypto.getRandomValues is not available');
  }
  return crypto.getRandomValues(new Uint8Array(1))[0] & 0xf;
};

const randomMath = (): number => Math.floor(Math.random() * 16);

export const uuidv4 = (): string => {
  const native = uuidCrypto();
  if (native) return native;

  const getRandom = (c: string) => {
    const r = crypto?.getRandomValues !== undefined ? randomCrypto() : randomMath();
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  };

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, getRandom);
};

export const helpers = {
  uuidCrypto,
  randomCrypto,
  randomMath,
};
